import { router, usePage } from "@inertiajs/react"
import { CreditCardIcon, PlusIcon } from "lucide-react"
import { type ReactNode, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { SharedProps } from "@/types"

interface SubscriptionRow {
  id: number
  plan: string
  status: string
  started_at: string | null
  expires_at: string | null
  price_cents: number | null
  currency: string | null
  update_url: string
  property: { id: number; name: string }
}

interface PropertyOption {
  id: number
  name: string
}

interface Props {
  subscriptions: SubscriptionRow[]
  meta: { page: number; pages: number; count: number }
  can_manage: boolean
  properties: PropertyOption[]
  urls: { create: string }
}

const PLANS = ["basic", "standard", "premium"] as const
const STATUSES = ["trial", "active", "cancelled", "expired"] as const

function planVariant(plan: string): "default" | "secondary" | "outline" {
  switch (plan) {
    case "premium":  return "default"
    case "standard": return "secondary"
    default:         return "outline"
  }
}

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "active":    return "default"
    case "trial":     return "secondary"
    case "cancelled": return "destructive"
    default:          return "outline"
  }
}

interface SubscriptionFormData {
  property_id: string
  plan: string
  status: string
  started_at: string
  expires_at: string
  price_cents: string
  currency: string
}

const EMPTY_FORM: SubscriptionFormData = {
  property_id: "",
  plan:        "basic",
  status:      "trial",
  started_at:  new Date().toISOString().slice(0, 16),
  expires_at:  "",
  price_cents: "0",
  currency:    "EUR",
}

function toFormData(s: SubscriptionRow): SubscriptionFormData {
  return {
    property_id: String(s.property.id),
    plan:        s.plan,
    status:      s.status,
    started_at:  s.started_at ? s.started_at.slice(0, 16) : "",
    expires_at:  s.expires_at ? s.expires_at.slice(0, 16) : "",
    price_cents: s.price_cents != null ? String(s.price_cents) : "0",
    currency:    s.currency ?? "EUR",
  }
}

interface SubscriptionDialogProps {
  open: boolean
  onClose: () => void
  subscription: SubscriptionRow | null
  properties: PropertyOption[]
  submitUrl: string
  method: "post" | "patch"
}

function SubscriptionDialog({ open, onClose, subscription, properties, submitUrl, method }: SubscriptionDialogProps) {
  const { errors } = usePage<SharedProps>().props
  const [form, setForm] = useState<SubscriptionFormData>(
    subscription ? toFormData(subscription) : EMPTY_FORM,
  )

  function field(key: keyof SubscriptionFormData, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = {
      subscription: {
        property_id: form.property_id,
        plan:        form.plan,
        status:      form.status,
        started_at:  form.started_at,
        expires_at:  form.expires_at || null,
        price_cents: form.price_cents ? parseInt(form.price_cents, 10) : null,
        currency:    form.currency || null,
      },
    }
    if (method === "patch") {
      router.patch(submitUrl, payload, { onSuccess: onClose })
    } else {
      router.post(submitUrl, payload, { onSuccess: onClose })
    }
  }

  const title = subscription ? `Edit subscription — ${subscription.property.name}` : "New subscription"

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 pt-2">
          {!subscription && (
            <div className="grid gap-1.5">
              <Label htmlFor="sub-property">Property</Label>
              <Select value={form.property_id} onValueChange={(v) => field("property_id", v)}>
                <SelectTrigger id="sub-property">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.property_id && <p className="text-xs text-destructive">{errors.property_id}</p>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sub-plan">Plan</Label>
              <Select value={form.plan} onValueChange={(v) => field("plan", v)}>
                <SelectTrigger id="sub-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLANS.map((p) => (
                    <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.plan && <p className="text-xs text-destructive">{errors.plan}</p>}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="sub-status">Status</Label>
              <Select value={form.status} onValueChange={(v) => field("status", v)}>
                <SelectTrigger id="sub-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.status && <p className="text-xs text-destructive">{errors.status}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sub-started">Started</Label>
              <Input
                id="sub-started"
                type="datetime-local"
                value={form.started_at}
                onChange={(e) => field("started_at", e.target.value)}
              />
              {errors.started_at && <p className="text-xs text-destructive">{errors.started_at}</p>}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="sub-expires">Expires</Label>
              <Input
                id="sub-expires"
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => field("expires_at", e.target.value)}
              />
              {errors.expires_at && <p className="text-xs text-destructive">{errors.expires_at}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sub-price">Price (cents)</Label>
              <Input
                id="sub-price"
                type="number"
                min="0"
                value={form.price_cents}
                onChange={(e) => field("price_cents", e.target.value)}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="sub-currency">Currency</Label>
              <Input
                id="sub-currency"
                value={form.currency}
                onChange={(e) => field("currency", e.target.value)}
                placeholder="EUR"
                maxLength={3}
              />
            </div>
          </div>

          {errors.base && <p className="text-xs text-destructive">{errors.base}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit">{subscription ? "Save changes" : "Create"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function Subscriptions({ subscriptions, meta, can_manage, properties, urls }: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<SubscriptionRow | null>(null)

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Subscriptions</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {meta.count} subscription{meta.count !== 1 ? "s" : ""} · Manual plan management.
          </p>
        </div>
        {can_manage && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <PlusIcon className="mr-1.5 size-4" />
            New subscription
          </Button>
        )}
      </div>

      {subscriptions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border py-20 text-center">
          <CreditCardIcon className="mb-4 h-10 w-10 text-muted-foreground opacity-30" aria-hidden="true" />
          <p className="text-sm font-medium text-muted-foreground">No subscriptions yet.</p>
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Expires</TableHead>
                {can_manage && <TableHead className="w-16"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell className="font-medium">{sub.property.name}</TableCell>
                  <TableCell>
                    <Badge variant={planVariant(sub.plan)} className="capitalize">{sub.plan}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(sub.status)} className="capitalize">{sub.status}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}
                  </TableCell>
                  {can_manage && (
                    <TableCell>
                      <button
                        className="text-sm text-primary hover:underline"
                        onClick={() => setEditing(sub)}
                      >
                        Edit
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {can_manage && (
        <>
          <SubscriptionDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            subscription={null}
            properties={properties}
            submitUrl={urls.create}
            method="post"
          />

          {editing && (
            <SubscriptionDialog
              open
              onClose={() => setEditing(null)}
              subscription={editing}
              properties={properties}
              submitUrl={editing.update_url}
              method="patch"
            />
          )}
        </>
      )}
    </div>
  )
}

Subscriptions.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
